/**
 * 补救请求的幂等键：**同一次意图复用同一把**。
 *
 * # 为什么不能每次点击现生成
 *
 * 因为「提交成功但响应丢了」这一种，长得和「没提交成功」一模一样。
 * 用户看到的是一个没反应的按钮，他会再点一次——而如果那一次带的是
 * 新钥匙，服务端会**真的再建一个替代 Session**。
 *
 * 于是这一项上有两个 live writer，而两次请求各自都是合法的。
 * 服务端那把 work item 行锁挡得住并发，挡不住这个：两次是先后来的，
 * 后一次进来时前一次已经提交了……如果前一次真的成了。
 * 挡它的唯一办法是**这两次带同一把钥匙**。
 *
 * # 什么时候才换一把
 *
 * 只有两种：**确认成功**，或者**收到一个明确的业务拒绝**（4xx/409）。
 * 前者说明这次意图完成了，后者说明这次意图不会成——两种都意味着
 * 「下一次点击是一个新意图」。
 *
 * 网络错误、超时、5xx **都不换**：那几种恰恰是「不知道成没成」，
 * 而不知道的时候换钥匙就是在赌。
 */

/** 一次补救意图。三样一起决定「是不是同一次」。 */
export interface RecoveryIntent {
  readonly subrunId: string
  readonly action: 'retry' | 'swap_model' | 'takeover'
  /** swap_model 的目标适配器。别的动作是 undefined。 */
  readonly adapter?: string
}

function intentKey(intent: RecoveryIntent): string {
  return `${intent.subrunId}|${intent.action}|${intent.adapter ?? ''}`
}

/**
 * 按意图发钥匙的小仓库。
 *
 * 做成一个对象而不是模块级的 Map，是为了让用例能开一个干净的——
 * 模块级的那种会让两条用例互相看见对方的钥匙，而那种串扰
 * 表现成「第二条用例莫名其妙拿到了重放」。
 */
export class RecoveryKeys {
  private readonly issued = new Map<string, string>()

  /** 拿这次意图的钥匙。同一个意图**每次都是同一把**。 */
  keyFor(intent: RecoveryIntent, mint: () => string = () => crypto.randomUUID()): string {
    const k = intentKey(intent)
    const existing = this.issued.get(k)
    if (existing !== undefined) return existing
    const fresh = mint()
    this.issued.set(k, fresh)
    return fresh
  }

  /**
   * 这次意图结束了，放掉它的钥匙。
   *
   * **只在确认成功或明确业务拒绝时调。** 网络错误不调——
   * 那时我们不知道服务端收没收到。
   */
  release(intent: RecoveryIntent): void {
    this.issued.delete(intentKey(intent))
  }

  /** 这次意图现在有没有一把在用的钥匙。用例用。 */
  has(intent: RecoveryIntent): boolean {
    return this.issued.has(intentKey(intent))
  }
}

/**
 * 一个错误算不算「明确的业务拒绝」。
 *
 * 判据是**服务端给出了一个它自己的判断**：4xx 说的是「这个请求不行」，
 * 而 5xx 与网络错误说的是「我没能告诉你行不行」。
 * 前者可以放钥匙，后者绝对不能。
 */
export function isDefinitiveRejection(status: number | undefined): boolean {
  return status !== undefined && status >= 400 && status < 500
}
